// Feature data hooks. Each fetches on mount, refetches when the window regains
// focus (so a partner's changes appear), and exposes optimistic mutators.
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";
import type {
  Bill,
  BillCategory,
  Budget,
  CalendarEvent,
  EventCategory,
  ExpenseTag,
  ShoppingItem,
  ShoppingList,
  TagColor,
} from "./meta";

function useFocusRefetch(reload: () => void) {
  const ref = useRef(reload);
  ref.current = reload;
  useEffect(() => {
    const onFocus = () => ref.current();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);
}

// ----- Calendar ------------------------------------------------------------

export interface NewEvent {
  title: string;
  date: string;
  time: string | null;
  allDay: boolean;
  who: string;
  category: EventCategory;
  recurrence: "none" | "daily" | "weekly" | "monthly" | "yearly";
}

export function useEvents(from: string, to: string) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const data = await api.get<CalendarEvent[]>(`/api/calendar/events?from=${from}&to=${to}`);
      setEvents(data ?? []);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void reload();
  }, [reload]);
  useFocusRefetch(reload);

  const addEvent = useCallback(async (e: NewEvent) => {
    await api.post("/api/calendar/events", e);
    await reload();
  }, [reload]);

  const deleteEvent = useCallback(async (id: string) => {
    setEvents((prev) => prev.filter((e) => e.id !== id));
    await api.del(`/api/calendar/events/${id}`);
    await reload();
  }, [reload]);

  return { events, loading, addEvent, deleteEvent, reload };
}

// ----- Bills ---------------------------------------------------------------

export interface NewBill {
  name: string;
  amount: number;
  dueDate: string;
  category: BillCategory;
  recurrence: "monthly" | "yearly";
}

export function useBills() {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const data = await api.get<Bill[]>("/api/bills");
      setBills(data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);
  useFocusRefetch(reload);

  const addBill = useCallback(async (b: NewBill) => {
    await api.post("/api/bills", b);
    await reload();
  }, [reload]);

  const setPaid = useCallback(async (id: string, paid: boolean) => {
    setBills((prev) => prev.map((b) => (b.id === id ? { ...b, paid } : b)));
    await api.patch(`/api/bills/${id}`, { paid });
    await reload();
  }, [reload]);

  const deleteBill = useCallback(async (id: string) => {
    setBills((prev) => prev.filter((b) => b.id !== id));
    await api.del(`/api/bills/${id}`);
    await reload();
  }, [reload]);

  return { bills, loading, addBill, setPaid, deleteBill, reload };
}

// ----- Shopping ------------------------------------------------------------

export function useShopping() {
  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [ls, its] = await Promise.all([
        api.get<ShoppingList[]>("/api/shopping/lists"),
        api.get<ShoppingItem[]>("/api/shopping/items"),
      ]);
      setLists(ls ?? []);
      setItems(its ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);
  useFocusRefetch(reload);

  const addItem = useCallback(async (listId: string, name: string, qty: string | null) => {
    await api.post("/api/shopping/items", { listId, name, qty });
    await reload();
  }, [reload]);

  const toggleItem = useCallback(async (id: string, checked: boolean) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, checked } : i)));
    await api.patch(`/api/shopping/items/${id}`, { checked });
    await reload();
  }, [reload]);

  const deleteItem = useCallback(async (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    await api.del(`/api/shopping/items/${id}`);
    await reload();
  }, [reload]);

  const clearChecked = useCallback(async (listId: string) => {
    setItems((prev) => prev.filter((i) => !(i.listId === listId && i.checked)));
    await api.post(`/api/shopping/clear?listId=${listId}`);
    await reload();
  }, [reload]);

  return { lists, items, loading, addItem, toggleItem, deleteItem, clearChecked, reload };
}

// ----- Finance -------------------------------------------------------------

/**
 * Expense tags and budgets. Mutators reject on failure (a duplicate name gives
 * an ApiError with status 409) so the page can surface the message.
 */
export function useFinance() {
  const [tags, setTags] = useState<ExpenseTag[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [ts, bs] = await Promise.all([
        api.get<ExpenseTag[]>("/api/finance/tags"),
        api.get<Budget[]>("/api/finance/budgets"),
      ]);
      setTags(ts ?? []);
      setBudgets(bs ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);
  useFocusRefetch(reload);

  const addTag = useCallback(async (name: string, color: TagColor) => {
    await api.post("/api/finance/tags", { name, color });
    await reload();
  }, [reload]);

  const updateTag = useCallback(async (id: string, patch: { name?: string; color?: TagColor }) => {
    await api.patch(`/api/finance/tags/${id}`, patch);
    await reload();
  }, [reload]);

  const deleteTag = useCallback(async (id: string) => {
    setTags((prev) => prev.filter((t) => t.id !== id));
    await api.del(`/api/finance/tags/${id}`);
    await reload();
  }, [reload]);

  const addBudget = useCallback(async (name: string) => {
    await api.post("/api/finance/budgets", { name });
    await reload();
  }, [reload]);

  const updateBudget = useCallback(async (id: string, patch: { name?: string }) => {
    await api.patch(`/api/finance/budgets/${id}`, patch);
    await reload();
  }, [reload]);

  const deleteBudget = useCallback(async (id: string) => {
    setBudgets((prev) => prev.filter((b) => b.id !== id));
    await api.del(`/api/finance/budgets/${id}`);
    await reload();
  }, [reload]);

  return {
    tags,
    budgets,
    loading,
    addTag,
    updateTag,
    deleteTag,
    addBudget,
    updateBudget,
    deleteBudget,
    reload,
  };
}
